import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDistanceToNow } from 'date-fns';
import { Bell, CheckCheck, Sparkles, TrendingUp, AlertCircle, Trophy } from 'lucide-react';
import { toast } from 'sonner';

type NotificationCategory = 'all' | 'community' | 'challenge' | 'milestone' | 'partnership';

export default function NotificationHistory() {
  const [selectedCategory, setSelectedCategory] = useState<NotificationCategory>('all');
  const queryClient = useQueryClient();

  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notification-history', selectedCategory],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (selectedCategory !== 'all') {
        query = query.eq('type', selectedCategory);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-history'] });
      toast.success('All notifications marked as read');
    }
  });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'community':
        return <Sparkles className="h-4 w-4" />;
      case 'challenge':
        return <Trophy className="h-4 w-4" />;
      case 'milestone':
        return <TrendingUp className="h-4 w-4" />;
      case 'partnership':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'community':
        return 'bg-purple-500/10 text-purple-500';
      case 'challenge':
        return 'bg-green-500/10 text-green-500';
      case 'milestone':
        return 'bg-blue-500/10 text-blue-500';
      case 'partnership':
        return 'bg-amber-500/10 text-amber-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  const unreadCount = notifications?.filter(n => !n.is_read).length || 0;

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Notification History</h1>
          <p className="text-muted-foreground">
            View and manage your notifications from Therappi®
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            onClick={() => markAllAsReadMutation.mutate()}
            disabled={markAllAsReadMutation.isPending}
            variant="outline"
          >
            <CheckCheck className="h-4 w-4 mr-2" />
            Mark all as read ({unreadCount})
          </Button>
        )}
      </div>

      <Tabs value={selectedCategory} onValueChange={(v) => setSelectedCategory(v as NotificationCategory)}>
        <TabsList className="mb-4">
          <TabsTrigger value="all">
            All {notifications?.length && `(${notifications.length})`}
          </TabsTrigger>
          <TabsTrigger value="community">
            <Sparkles className="h-4 w-4 mr-1" />
            Community
          </TabsTrigger>
          <TabsTrigger value="challenge">
            <Trophy className="h-4 w-4 mr-1" />
            Challenges
          </TabsTrigger>
          <TabsTrigger value="milestone">
            <Bell className="h-4 w-4 mr-1" />
            Milestones
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedCategory}>
          <ScrollArea className="h-[600px] pr-4">
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : notifications && notifications.length > 0 ? (
              <div className="space-y-4">
                {notifications.map((notification) => (
                  <Card 
                    key={notification.id}
                    className={`transition-all ${
                      !notification.is_read 
                        ? 'border-primary/50 bg-primary/5' 
                        : 'opacity-75'
                    }`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${getNotificationColor(notification.type)}`}>
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div>
                            <CardTitle className="text-lg">{notification.title}</CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-relaxed">{notification.message}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <Badge variant="outline" className="text-xs">
                          {notification.type.replace(/_/g, ' ')}
                        </Badge>
                        {!notification.is_read && (
                          <Badge variant="secondary" className="text-xs">
                            Unread
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Bell className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">No notifications yet</p>
                  <p className="text-sm text-muted-foreground text-center max-w-sm">
                    When you receive insights, reminders, or updates from Therappi®, they'll appear here.
                  </p>
                </CardContent>
              </Card>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
